package blazern.lexisoup.model.lexical_item_details_source.chatgpt

import arrow.core.Either
import arrow.core.Either.Left
import arrow.core.Either.Right
import arrow.core.getOrElse
import blazern.lexisoup.data.lexical_item_details_source.api.LexicalItemDetailsSource
import blazern.lexisoup.data.lexical_item_details_source.api.LexicalItemDetailsSource.Item
import blazern.lexisoup.data.lexical_item_details_source.utils.cache.LexicalItemDetailsSourceCacher
import blazern.lexisoup.data.lexisoup.graphql.LexisoupApolloClientHolder
import blazern.lexisoup.data.lexisoup.graphql.err
import blazern.lexisoup.data.lexisoup.graphql.mapSource
import blazern.lexisoup.data.lexisoup.graphql.toDomain
import blazern.lexisoup.domain.error.Err
import blazern.lexisoup.domain.model.DataSource
import blazern.lexisoup.domain.model.Lang
import blazern.lexisoup.domain.model.LexicalItemDetail
import blazern.lexisoup.domain.model.Sentence
import blazern.lexisoup.graphql.model.LexicalItemsFromLLMQuery
import com.apollographql.apollo.ApolloClient
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flow

class ChatGPTLexicalItemDetailsSource(
    private val apolloClientHolder: LexisoupApolloClientHolder,
    private val cacher: LexicalItemDetailsSourceCacher,
) : LexicalItemDetailsSource {
    private val apollo: Flow<ApolloClient>
        get() = apolloClientHolder.client

    override val source = DataSource.ChatGPT
    override val types = setOf(
        LexicalItemDetail.Type.FORMS,
        LexicalItemDetail.Type.WORD_TRANSLATIONS,
        LexicalItemDetail.Type.SYNONYMS,
        LexicalItemDetail.Type.EXPLANATION,
        LexicalItemDetail.Type.EXAMPLE,
    )

    override fun request(
        query: String,
        langFrom: Lang,
        langTo: Lang
    ): Flow<Item> = cacher.retrieveOrExecute(source, query, langFrom, langTo) {
        requestImpl(query, langFrom, langTo)
    }

    private fun requestImpl(
        query: String,
        langFrom: Lang,
        langTo: Lang
    ): Flow<Item> = flow {
        while (true) {
            val result = apolloRequest(query, langFrom, langTo)
            result.fold(
                { emit(Item.Failure(it)) },
                {
                    emit(Item.Page(
                        details = it,
                        nextPageTypes = types,
                    ))
                    return@flow
                }
            )
        }
    }

    private suspend fun apolloRequest(
        query: String,
        langFrom: Lang,
        langTo: Lang,
    ): Either<Err, List<LexicalItemDetail>> {
        val result = apollo.first().query(
            LexicalItemsFromLLMQuery(
                query,
                langFromIso3 = langFrom.iso3,
                langToIso3 = langTo.iso3,
            )
        ).execute()

        result.err?.let { return Left(it) }

        val data = result.data ?: return Right(emptyList())

        val items = data.llm.mapNotNull {
            it.toDomain(langFrom, langTo).getOrElse { return Left(Err.from(it)) }
        }
        return Right(items)
    }
}

private fun LexicalItemsFromLLMQuery.Llm.toDomain(
    langFrom: Lang,
    langTo: Lang,
): Either<IllegalArgumentException, LexicalItemDetail?> {
    onForms?.let {
        return Right(LexicalItemDetail.Forms(
            value = LexicalItemDetail.Forms.Value.Text(it.text),
            lang = langFrom,
            source = mapSource(it.source)
        ))
    }
    onExplanation?.let {
        return Right(LexicalItemDetail.Explanation(
            text = Sentence(it.text, langTo, mapSource(it.source)),
            source = mapSource(it.source)
        ))
    }
    onWordTranslations?.let {
        val ts = it.translationsSet.translationsSetFields
        return Right(LexicalItemDetail.WordTranslations(
            translationsSet = ts.toDomain().getOrElse { return Left(it) },
            source = mapSource(it.source)
        ))
    }
    onSynonyms?.let {
        val ts = it.translationsSet.translationsSetFields
        return Right(LexicalItemDetail.Synonyms(
            sentences = ts.toDomain()
                .map { it.translations }
                .getOrElse { return Left(it) },
            source = mapSource(it.source)
        ))
    }
    onExample?.let {
        val ts = it.translationsSet.translationsSetFields
        return Right(LexicalItemDetail.Example(
            translationsSet = ts.toDomain().getOrElse { return Left(it) },
            source = mapSource(it.source)
        ))
    }
    // New field in a newer version of the backend was added apparently
    return Right(null)
}
